import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  toScheduleView,
  useSchedulesApiClient,
  type ScheduleView,
} from '@/entities/schedule';
import { scheduleKeys } from '@/entities/schedule';

export type UseSchedulesQueryOptions = {
  agentId?: string;
  includeCancelled?: boolean;
  limit?: number;
  offset?: number;
  enabled?: boolean;
};

export type SchedulesListView = {
  schedules: ReadonlyArray<ScheduleView>;
  totalCount: number;
  returnedCount: number;
};

export function useSchedulesQuery({
  agentId,
  includeCancelled = false,
  limit = 10,
  offset = 0,
  enabled = true,
}: UseSchedulesQueryOptions = {}): UseQueryResult<SchedulesListView, Error> {
  const client = useSchedulesApiClient();
  const params = { agentId, includeCancelled, limit, offset };

  return useQuery<SchedulesListView, Error>({
    enabled,
    queryKey: scheduleKeys.list(params),
    queryFn: async ({ signal }) => {
      const raw = await client.listSchedules({
        agentId,
        includeCancelled,
        limit,
        offset,
        signal,
      });
      const schedules = raw.schedules.flatMap((schedule) => {
        const view = toScheduleView(schedule);
        return view === null ? [] : [view];
      });
      return {
        schedules,
        totalCount: raw.total_count,
        returnedCount: raw.returned_count,
      };
    },
  });
}
